import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Calendar, 
  CheckCircle, 
  Clock, 
  DollarSign, 
  TrendingUp,
  Package,
  AlertCircle,
  XCircle,
  Loader2,
  Users,
  Activity,
  Phone,
  CreditCard,
  Edit,
  Trash2,
  UserPlus,
  LogIn,
  Eye,
  MapPin,
  RefreshCw,
  HardDrive,
  Cloud,
  Link2,
  ExternalLink,
  RefreshCcw,
  Truck
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { safeFetch } from '../../utils/safeFetch';

// Activity event icons
const ACTIVITY_ICONS = {
  otp_requested: Phone,
  otp_verified: CheckCircle,
  booking_created: Calendar,
  booking_payment: CreditCard,
  lead_created: UserPlus,
  quote_viewed: Eye,
  status_changed: RefreshCw,
  booking_updated: Edit,
  booking_deleted: Trash2,
  finance_updated: DollarSign,
  schedule_updated: Calendar,
  location_updated: MapPin,
  client_created: UserPlus,
  client_updated: Users,
  admin_login: LogIn,
};

const ACTIVITY_COLORS = {
  website: 'bg-blue-50 text-blue-600',
  admin: 'bg-purple-50 text-purple-600',
  other: 'bg-gray-50 text-gray-600',
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activityStats, setActivityStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all'); // all, website, admin
  
  // Google Drive state
  const [driveStatus, setDriveStatus] = useState(null);
  const [driveLoading, setDriveLoading] = useState(true);
  const [connectingDrive, setConnectingDrive] = useState(false);
  const [weeklyTrends, setWeeklyTrends] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchActivities();
    fetchActivityStats();
    fetchDriveStatus();
    fetchWeeklyTrends();
    
    // Check for drive connection success/error in URL
    if (searchParams.get('drive_connected') === 'true') {
      alert('Google Drive connected successfully!');
      window.history.replaceState({}, '', '/admin/dashboard');
    }
    if (searchParams.get('drive_error')) {
      alert('Google Drive connection failed: ' + searchParams.get('drive_error'));
      window.history.replaceState({}, '', '/admin/dashboard');
    }
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [activityFilter]);

  const fetchDriveStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/oauth/drive/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) {
        setDriveStatus(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch drive status:', err);
    } finally {
      setDriveLoading(false);
    }
  };

  const connectGoogleDrive = async () => {
    setConnectingDrive(true);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/oauth/drive/connect`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (result.ok) {
        window.location.href = result.data.authorization_url;
      } else {
        alert('Failed to initiate Google Drive connection');
      }
    } catch (err) {
      alert('Connection error: ' + err.message);
    } finally {
      setConnectingDrive(false);
    }
  };

  const disconnectGoogleDrive = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Drive?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/oauth/drive/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (result.ok) {
        setDriveStatus({ connected: false });
        alert('Google Drive disconnected');
      }
    } catch (err) {
      alert('Failed to disconnect');
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!result.ok) throw new Error('Failed to fetch stats');
      
      setStats(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      let url = `${API_URL}/api/activity/?limit=50&days=7`;
      if (activityFilter !== 'all') {
        url += `&category=${activityFilter}`;
      }
      
      const result = await safeFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (result.ok) {
        setActivities(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch activities:', err);
    }
  };

  const fetchActivityStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/activity/stats?days=7`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (result.ok) {
        setActivityStats(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch activity stats:', err);
    }
  };

  const fetchWeeklyTrends = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/admin/weekly-trends?weeks=12`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) {
        setWeeklyTrends(result.data.trends || []);
      }
    } catch (err) {
      console.error('Failed to fetch weekly trends:', err);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'Australia/Melbourne' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0D9488]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Bookings',
      value: stats.total_bookings,
      icon: Calendar,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      label: 'Pending',
      value: stats.pending_bookings,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      label: 'Confirmed',
      value: stats.confirmed_bookings,
      icon: CheckCircle,
      color: 'text-[#0D9488]',
      bgColor: 'bg-[#F0FDFA]'
    },
    {
      label: 'In Progress',
      value: stats.in_progress_bookings,
      icon: Package,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      label: 'Completed',
      value: stats.completed_bookings,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      label: 'Cancelled',
      value: stats.cancelled_bookings,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-[#111111]">Dashboard</h1>
        <p className="text-[#64748B] mt-1">Overview of your business performance</p>
      </div>

      {/* Revenue Card */}
      <Card className="bg-gradient-to-r from-[#0D9488] to-[#0F766E] border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium">Total Revenue</p>
              <p className="text-4xl font-bold text-white mt-2">
                ${stats.total_revenue.toFixed(2)}
              </p>
              <p className="text-white/80 text-sm mt-2">
                From {stats.completed_bookings} completed bookings
              </p>
            </div>
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
              <DollarSign className="w-8 h-8 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-white border-[#E2E8F0] shadow-sm">
            <CardContent className="p-4">
              <div className={`w-10 h-10 rounded-lg ${stat.bgColor} flex items-center justify-center mb-3`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <p className="text-2xl font-bold text-[#111111]">{stat.value}</p>
              <p className="text-[#64748B] text-sm">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Weekly Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#F0FDFA] flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#0D9488]" />
              </div>
              <div>
                <p className="text-[#64748B] text-xs font-medium">Today</p>
                <p className="text-[#111111] text-sm font-semibold">Bookings Received</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-[#111111]">{stats.bookings_today}</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-[#64748B] text-xs font-medium">This Week</p>
                <p className="text-[#111111] text-sm font-semibold">Bookings Received</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-[#111111]">{stats.v2_bookings_this_week || stats.bookings_this_week}</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                <Truck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-[#64748B] text-xs font-medium">This Week</p>
                <p className="text-[#111111] text-sm font-semibold">Collections Done</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-[#111111]">{stats.collected_this_week || 0}</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-[#64748B] text-xs font-medium">Total</p>
                <p className="text-[#111111] text-sm font-semibold">Active Clients</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-[#111111]">{stats.total_clients || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Trends Chart */}
      {weeklyTrends.length > 0 && (
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0D9488] to-[#0F766E] flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-[#111111] font-semibold">Weekly Trends</h3>
                <p className="text-[#64748B] text-sm">Bookings received vs collections done (last 12 weeks)</p>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weeklyTrends} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="week" 
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    tickLine={false}
                    axisLine={{ stroke: '#E2E8F0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    tickLine={false}
                    axisLine={{ stroke: '#E2E8F0' }}
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0', 
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                    labelStyle={{ fontWeight: 600, color: '#111111', marginBottom: 4 }}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    iconType="circle"
                    formatter={(value) => <span style={{ color: '#64748B', fontSize: 13 }}>{value}</span>}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="bookings" 
                    name="Bookings Received"
                    stroke="#0D9488" 
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: '#0D9488', strokeWidth: 0 }}
                    activeDot={{ r: 6, fill: '#0D9488', strokeWidth: 2, stroke: '#fff' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="collections" 
                    name="Collections Done"
                    stroke="#22C55E" 
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: '#22C55E', strokeWidth: 0 }}
                    activeDot={{ r: 6, fill: '#22C55E', strokeWidth: 2, stroke: '#fff' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Google Drive Storage Card */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                driveStatus?.connected ? 'bg-green-50' : 'bg-gray-100'
              }`}>
                <Cloud className={`w-6 h-6 ${driveStatus?.connected ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-[#111111] font-semibold flex items-center gap-2">
                  Google Drive Storage
                  {driveStatus?.connected && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">Connected</span>
                  )}
                </h3>
                {driveLoading ? (
                  <p className="text-[#64748B] text-sm">Checking connection...</p>
                ) : driveStatus?.connected ? (
                  <p className="text-[#64748B] text-sm">
                    Connected as {driveStatus.user_email}
                  </p>
                ) : (
                  <p className="text-[#64748B] text-sm">Connect to store files persistently</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {driveLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-[#64748B]" />
              ) : driveStatus?.connected ? (
                <>
                  <Button
                    data-testid="open-google-drive-btn"
                    variant="default"
                    size="sm"
                    onClick={() => {
                      const url = driveStatus.folder_url || 'https://drive.google.com';
                      window.open(url, '_blank');
                    }}
                    className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
                  >
                    <ExternalLink className="w-4 h-4 mr-1.5" />
                    Open Google Drive
                  </Button>
                  <Button
                    data-testid="change-google-drive-btn"
                    variant="outline"
                    size="sm"
                    onClick={connectGoogleDrive}
                    disabled={connectingDrive}
                    className="border-[#E2E8F0] text-[#64748B] hover:bg-gray-50"
                  >
                    {connectingDrive ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCcw className="w-4 h-4 mr-1.5" />
                        Change
                      </>
                    )}
                  </Button>
                  <Button
                    data-testid="disconnect-google-drive-btn"
                    variant="outline"
                    size="sm"
                    onClick={disconnectGoogleDrive}
                    className="border-red-200 text-red-600 hover:bg-red-50"
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button
                  data-testid="connect-google-drive-btn"
                  onClick={connectGoogleDrive}
                  disabled={connectingDrive}
                  className="bg-[#0D9488] hover:bg-[#0F766E]"
                >
                  {connectingDrive ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4 mr-2" />
                      Connect Google Drive
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Log Section */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0D9488] to-[#0F766E] flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-[#111111] font-semibold">Activity Log</h3>
                <p className="text-[#64748B] text-sm">
                  {activityStats?.total_activities || 0} events in the last 7 days
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={activityFilter === 'all' ? 'default' : 'outline'}
                className={activityFilter === 'all' ? 'bg-[#0D9488] hover:bg-[#0F766E]' : ''}
                onClick={() => setActivityFilter('all')}
              >
                All
              </Button>
              <Button
                size="sm"
                variant={activityFilter === 'website' ? 'default' : 'outline'}
                className={activityFilter === 'website' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                onClick={() => setActivityFilter('website')}
              >
                Website
              </Button>
              <Button
                size="sm"
                variant={activityFilter === 'admin' ? 'default' : 'outline'}
                className={activityFilter === 'admin' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                onClick={() => setActivityFilter('admin')}
              >
                Admin
              </Button>
            </div>
          </div>

          {/* Activity Stats Summary */}
          {activityStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-blue-600 text-xs font-medium">Website Events</p>
                <p className="text-blue-800 text-lg font-bold">{activityStats.by_category?.website || 0}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3">
                <p className="text-purple-600 text-xs font-medium">Admin Events</p>
                <p className="text-purple-800 text-lg font-bold">{activityStats.by_category?.admin || 0}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-green-600 text-xs font-medium">OTP Requests</p>
                <p className="text-green-800 text-lg font-bold">
                  {activityStats.by_event_type?.find(e => e.event_type === 'otp_requested')?.count || 0}
                </p>
              </div>
              <div className="bg-teal-50 rounded-lg p-3">
                <p className="text-teal-600 text-xs font-medium">Bookings Created</p>
                <p className="text-teal-800 text-lg font-bold">
                  {activityStats.by_event_type?.find(e => e.event_type === 'booking_created')?.count || 0}
                </p>
              </div>
            </div>
          )}

          {/* Activity List */}
          <div className="max-h-96 overflow-y-auto border border-[#E2E8F0] rounded-lg">
            {activities.length === 0 ? (
              <div className="p-8 text-center text-[#94A3B8]">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No activity recorded yet</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E2E8F0]">
                {activities.map((activity, idx) => {
                  const IconComponent = ACTIVITY_ICONS[activity.event_type] || Activity;
                  const colorClass = ACTIVITY_COLORS[activity.category] || ACTIVITY_COLORS.other;
                  
                  return (
                    <div 
                      key={activity.id || idx} 
                      className="p-3 hover:bg-[#F8FAFC] transition-colors flex items-start gap-3 cursor-pointer"
                      onClick={() => activity.booking_ref && navigate(`/admin/bookings?ref=${activity.booking_ref}`)}
                    >
                      <div className={`w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center flex-shrink-0`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-[#111111] truncate">
                            {activity.description}
                          </p>
                          <span className="text-xs text-[#94A3B8] whitespace-nowrap">
                            {formatTime(activity.timestamp)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {activity.booking_ref && (
                            <span className="text-xs bg-[#F0FDFA] text-[#0D9488] px-2 py-0.5 rounded font-mono">
                              {activity.booking_ref}
                            </span>
                          )}
                          {activity.user_email && (
                            <span className="text-xs text-[#94A3B8]">{activity.user_email}</span>
                          )}
                          {activity.user_phone && !activity.user_email && (
                            <span className="text-xs text-[#94A3B8]">{activity.user_phone}</span>
                          )}
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            activity.category === 'website' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {activity.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
